import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  RefreshCw,
  Pencil,
  Trash2,
  X,
  Save,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Zap
} from 'lucide-react';
import SetPaidTrafficModal from './SetPaidTrafficModal';
import DomainCheckBar from './DomainCheckBar';
import { channelMeta, resolvePaidChannels } from '../../services/paidChannels';
import VerticalPicker from './VerticalPicker';
import GeoTrafficEditor from './GeoTrafficEditor';
import {
  VERTICAL_OPTIONS,
  parseVerticals,
  formatVerticals,
  parseGeoRows,
  formatGeoRows
} from './affiliateFields';

const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

const PAGE_SIZES = [25, 50, 100, 200];

// Fields the edit modal can write — must stay in sync with EDITABLE_FIELDS in
// mars-backend/controllers/paidTraffic/paidTraffic.js
//
// 'verticals' and 'geos' are pickers rather than free text: both columns are
// comma-joined vocabularies where a typo produces a row no filter elsewhere in
// the app can match, and the geo one writes three columns that have to stay
// aligned with each other.
const EDIT_FIELDS = [
  { key: 'affiliate_url', label: 'Affiliate URL', type: 'text' },
  { key: 'organic_traffic', label: 'Organic Traffic', type: 'number' },
  { key: 'adwords_traffic', label: 'Paid Traffic', type: 'number' },
  { key: 'vertical', label: 'Vertical', type: 'verticals' },
  { key: 'top_geos', label: 'Countries & Traffic', type: 'geos' },
  { key: 'top_page', label: 'Top Pages', type: 'textarea' },
  { key: 'keywords', label: 'Top 5 Keywords', type: 'textarea' },
  { key: 'paid_keyword', label: 'Top Paid Keywords', type: 'textarea' },
  { key: 'paid_top_page', label: 'Paid Top Pages', type: 'textarea' }
];

// Written by the pickers, not by the generic inputs above.
const PICKER_KEYS = new Set(['vertical', 'top_geos']);

const fmtNumber = (n) =>
  n === null || n === undefined || n === '' ? '—' : Number(n).toLocaleString();

// The pipeline stores these as comma-joined strings.
const splitList = (value) =>
  !value ? [] : String(value).split(',').map((v) => v.trim()).filter(Boolean);

/** Comma-joined text rendered as a few pills plus a "+N" overflow marker. */
const ListCell = ({ value, limit = 3, tone = 'slate' }) => {
  const items = splitList(value);
  if (items.length === 0) return <span className="text-gray-300 dark:text-slate-600">—</span>;

  const tones = {
    slate: 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
  };

  return (
    <div className="flex flex-wrap gap-1" title={items.join('\n')}>
      {items.slice(0, limit).map((item, i) => (
        <span
          key={i}
          className={`inline-block max-w-[190px] truncate rounded px-1.5 py-0.5 text-[11px] ${tones[tone]}`}
        >
          {item}
        </span>
      ))}
      {items.length > limit && (
        <span className="inline-block rounded px-1.5 py-0.5 text-[11px] text-gray-500 dark:text-slate-400">
          +{items.length - limit}
        </span>
      )}
    </div>
  );
};

const SortHeader = ({ label, column, sortBy, sortDir, onSort, align = 'left' }) => {
  const active = sortBy === column;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`flex w-full items-center gap-1 ${align === 'right' ? 'justify-end' : ''} ${
        active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-slate-300'
      } hover:text-blue-600 dark:hover:text-blue-400`}
    >
      <span>{label}</span>
      <Icon className="h-3 w-3 flex-shrink-0" />
    </button>
  );
};

const PaidTraffic = () => {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [paidOnly, setPaidOnly] = useState(false);
  const [sortBy, setSortBy] = useState('organic_traffic');
  const [sortDir, setSortDir] = useState('desc');

  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Picker state for the two structured fields.
  const [verticalState, setVerticalState] = useState({ selected: [], custom: [] });
  const [geoRows, setGeoRows] = useState([]);

  // Vocabulary for the pickers. Country names are derived from the table itself,
  // so they are fetched once and reused for every row edited afterwards.
  const [options, setOptions] = useState({ verticals: VERTICAL_OPTIONS, countries: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const optionsLoaded = useRef(false);

  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteText, setDeleteText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Row currently open in the Set-paid-traffic modal.
  const [trafficRow, setTrafficRow] = useState(null);

  const debounceRef = useRef(null);

  // Deep link from Add Affiliate ("View it in Paid Traffic") lands with ?search=<domain>.
  useEffect(() => {
    const initial = new URLSearchParams(window.location.search).get('search');
    if (initial) setSearchInput(initial);
  }, []);

  // Debounce the search box so typing doesn't fire a query per keystroke.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        sortBy,
        sortDir
      });
      if (search) params.set('search', search);
      if (paidOnly) params.set('paidOnly', 'true');

      const res = await fetch(`${basePath}/api/paid-traffic/list?${params.toString()}`, {
        credentials: 'include'
      });

      if (res.status === 403) {
        setRows([]);
        setPagination(null);
        setError('Access denied — this page is available to the admin account only.');
        return;
      }

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Server error: ${res.status}`);
      }

      setRows(data.data || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message || 'Failed to load affiliates');
      setRows([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, paidOnly, sortBy, sortDir]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('desc');
    }
    setPage(1);
  };

  // Loaded lazily — nothing needs it until an edit modal is opened.
  const loadOptions = useCallback(async () => {
    if (optionsLoaded.current) return;
    optionsLoaded.current = true;
    setOptionsLoading(true);
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/options`, { credentials: 'include' });
      const data = await res.json();
      if (data.success && data.data) {
        setOptions({
          verticals: data.data.verticals?.length ? data.data.verticals : VERTICAL_OPTIONS,
          countries: data.data.countries || []
        });
      }
    } catch {
      // Leave the canonical verticals in place; the country box shows an empty
      // list rather than blocking the edit.
      optionsLoaded.current = false;
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const openEdit = (row) => {
    setEditRow(row);
    setEditError('');
    setEditForm(
      EDIT_FIELDS.filter((f) => !PICKER_KEYS.has(f.key)).reduce((acc, f) => {
        acc[f.key] = row[f.key] === null || row[f.key] === undefined ? '' : String(row[f.key]);
        return acc;
      }, {})
    );
    setVerticalState(parseVerticals(row.vertical));
    setGeoRows(parseGeoRows(row.top_geos, row.top_geo_organic_traffic, row.top_geo_adwords_traffic));
    loadOptions();
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!editForm.affiliate_url?.trim()) {
      setEditError('Affiliate URL cannot be empty');
      return;
    }

    // The pickers own vertical and the three geo columns; everything else comes
    // straight off the plain inputs.
    const payload = {
      ...editForm,
      vertical: formatVerticals(verticalState.selected, verticalState.custom),
      ...formatGeoRows(geoRows)
    };

    setSaving(true);
    setEditError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/${editRow.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Server error: ${res.status}`);
      }

      // Patch the row in place so the table doesn't jump back to page 1.
      setRows((prev) => prev.map((r) => (r.id === data.data.id ? data.data : r)));
      setNotice(`Updated ${data.data.affiliate_url}`);
      setEditRow(null);
    } catch (err) {
      setEditError(err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow || deleteText !== 'DELETE') return;

    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`${basePath}/api/paid-traffic/${deleteRow.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || `Server error: ${res.status}`);
      }

      setNotice(`Deleted ${deleteRow.affiliate_url}`);
      setDeleteRow(null);
      setDeleteText('');
      fetchData();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const closeDelete = () => {
    setDeleteRow(null);
    setDeleteText('');
    setDeleteError('');
  };

  // Patch the row in place if it is on the current page; otherwise just report it.
  // updatedRow is null when the modal corrected a log entry rather than the
  // affiliate — there is a message to show but no row to merge.
  const handlePaidTrafficSaved = (updatedRow, message) => {
    if (updatedRow) {
      setRows((prev) => prev.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)));
      setTrafficRow((prev) => (prev && prev.id === updatedRow.id ? { ...prev, ...updatedRow } : prev));
    }
    setNotice(message);
  };

  const rangeLabel = useMemo(() => {
    if (!pagination || pagination.total === 0) return 'No results';
    const from = pagination.offset + 1;
    const to = Math.min(pagination.offset + pageSize, pagination.total);
    return `${from.toLocaleString()}–${to.toLocaleString()} of ${pagination.total.toLocaleString()}`;
  }, [pagination, pageSize]);

  const totalPages = pagination?.totalPages || 1;

  return (
    <div className="mx-auto max-w-[1600px]">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100">Paid Traffic</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
          Every affiliate in <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-slate-800">affiliate_clean</code>,
          with organic and paid traffic, top pages and keywords. Edit or delete rows in place.
        </p>
      </div>

      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300">
          {notice}
        </div>
      )}

      <DomainCheckBar
        onSetPaidTraffic={(row) => setTrafficRow(row)}
        onAdded={(row, message) => {
          setNotice(message);
          fetchData();
        }}
      />

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search affiliate URL or keywords…"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          <input
            type="checkbox"
            checked={paidOnly}
            onChange={(e) => {
              setPaidOnly(e.target.checked);
              setPage(1);
            }}
            className="h-4 w-4 accent-blue-600"
          />
          Paid traffic only
        </label>

        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s} / page
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide dark:border-slate-700 dark:bg-slate-800/60">
              <tr>
                <th className="px-4 py-3">
                  <SortHeader label="Affiliate URL" column="affiliate_url" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                </th>
                <th className="w-32 px-4 py-3">
                  <SortHeader label="Organic" column="organic_traffic" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                </th>
                <th className="w-32 px-4 py-3">
                  <SortHeader label="Paid" column="adwords_traffic" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                </th>
                <th className="px-4 py-3 text-gray-600 dark:text-slate-300">Top Pages</th>
                <th className="px-4 py-3 text-gray-600 dark:text-slate-300">Top 5 Keywords</th>
                <th className="px-4 py-3 text-gray-600 dark:text-slate-300">Top Paid Keywords</th>
                <th className="w-32 px-4 py-3 text-right text-gray-600 dark:text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-gray-500 dark:text-slate-400">
                    <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                    Loading affiliates…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && !error && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-gray-500 dark:text-slate-400">
                    No affiliates match this filter.
                  </td>
                </tr>
              )}

              {rows.map((row) => (
                <tr key={row.id} className="align-top hover:bg-gray-50/70 dark:hover:bg-slate-800/40">
                  <td className="px-4 py-3">
                    <a
                      href={`https://${row.affiliate_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
                    >
                      {row.affiliate_url}
                      <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-60" />
                    </a>
                    {row.vertical && (
                      <div className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">{row.vertical}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-slate-300">
                    {fmtNumber(row.organic_traffic)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span
                      className={
                        Number(row.adwords_traffic) > 0
                          ? 'font-medium text-emerald-600 dark:text-emerald-400'
                          : 'text-gray-400 dark:text-slate-500'
                      }
                    >
                      {fmtNumber(row.adwords_traffic)}
                    </span>
                    {/* Only badge values this team actually set — untouched rows are SEMrush data. */}
                    {row.paid_traffic_event?.matches_current && (
                      <div
                        className="mt-0.5"
                        title={[
                          row.paid_traffic_event.source === 'generated' ? 'Generated value' : 'Entered manually',
                          channelMeta(row.paid_traffic_event.channel)?.where,
                          row.paid_traffic_event.keyword && `keyword: ${row.paid_traffic_event.keyword}`,
                          row.paid_traffic_event.advertiser_name &&
                            `advertiser: ${row.paid_traffic_event.advertiser_name}`,
                          row.paid_traffic_event.platforms && `platforms: ${row.paid_traffic_event.platforms}`,
                          row.paid_traffic_event.country && `country: ${row.paid_traffic_event.country}`,
                          row.paid_traffic_event.created_by
                        ]
                          .filter(Boolean)
                          .join('\n')}
                      >
                        <span className="inline-flex items-center gap-1">
                          {/* Where it was found, next to how it got here. */}
                          {resolvePaidChannels(row.paid_channels, row.adwords_traffic)
                            .map(channelMeta)
                            .filter(Boolean)
                            .map((channel) => (
                              <span
                                key={channel.key}
                                className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] font-bold ${channel.solid}`}
                              >
                                {channel.letter}
                              </span>
                            ))}
                          <span
                            className={`rounded px-1 py-0.5 text-[10px] font-medium uppercase ${
                              row.paid_traffic_event.source === 'generated'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400'
                            }`}
                          >
                            {row.paid_traffic_event.source}
                          </span>
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ListCell value={row.top_page} tone="slate" />
                  </td>
                  <td className="px-4 py-3">
                    <ListCell value={row.keywords} tone="blue" />
                  </td>
                  <td className="px-4 py-3">
                    <ListCell value={row.paid_keyword} tone="amber" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setTrafficRow(row)}
                        title="Set paid traffic"
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-amber-50 hover:text-amber-600 dark:text-slate-400 dark:hover:bg-amber-900/20 dark:hover:text-amber-400"
                      >
                        <Zap className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        title="Edit"
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-blue-50 hover:text-blue-600 dark:text-slate-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDeleteRow(row);
                          setDeleteText('');
                          setDeleteError('');
                        }}
                        title="Delete"
                        className="rounded-lg p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm dark:border-slate-700">
          <span className="text-gray-500 dark:text-slate-400">{rangeLabel}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 text-gray-600 dark:text-slate-300">
              Page {page.toLocaleString()} of {totalPages.toLocaleString()}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Set paid traffic + history */}
      {trafficRow && (
        <SetPaidTrafficModal
          row={trafficRow}
          onClose={() => setTrafficRow(null)}
          onSaved={handlePaidTrafficSaved}
        />
      )}

      {/* Edit modal */}
      {editRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Edit affiliate</h2>
              <button
                type="button"
                onClick={() => setEditRow(null)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              {EDIT_FIELDS.map((field) => (
                <div key={field.key}>
                  <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {field.label}
                  </label>
                  {field.type === 'verticals' ? (
                    <VerticalPicker
                      options={options.verticals}
                      selected={verticalState.selected}
                      custom={verticalState.custom}
                      onChange={setVerticalState}
                    />
                  ) : field.type === 'geos' ? (
                    <GeoTrafficEditor
                      rows={geoRows}
                      options={options.countries}
                      loadingOptions={optionsLoading}
                      onChange={setGeoRows}
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea
                      rows={2}
                      value={editForm[field.key] ?? ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, [field.key]: e.target.value }))}
                      placeholder="Comma-separated"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                  ) : (
                    <input
                      type={field.type}
                      min={field.type === 'number' ? 0 : undefined}
                      value={editForm[field.key] ?? ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, [field.key]: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                  )}
                </div>
              ))}

              {editError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
                  {editError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setEditRow(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — requires typing DELETE */}
      {deleteRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-slate-700">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600 dark:text-red-400">
                <AlertTriangle className="h-5 w-5" />
                Delete affiliate
              </h2>
              <button
                type="button"
                onClick={closeDelete}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <p className="text-sm text-gray-600 dark:text-slate-300">
                This permanently removes{' '}
                <span className="font-semibold text-gray-900 dark:text-slate-100">{deleteRow.affiliate_url}</span>{' '}
                (id {deleteRow.id}) from <code className="text-xs">affiliate_clean</code>. This cannot be undone.
              </p>

              <div>
                <label className="mb-1 block text-sm text-gray-600 dark:text-slate-300">
                  Type <span className="font-mono font-semibold text-red-600 dark:text-red-400">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteText}
                  autoFocus
                  onChange={(e) => setDeleteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && deleteText === 'DELETE') confirmDelete();
                  }}
                  placeholder="DELETE"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none focus:border-red-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>

              {deleteError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-300">
                  {deleteError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-slate-700">
              <button
                type="button"
                onClick={closeDelete}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteText !== 'DELETE' || deleting}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaidTraffic;
